import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { isMobile } from "react-device-detect";
import { ReactComponent as ChevronUpIcon } from "../../Assets/icons/dropDownArrowNoColor.svg";

import { ReactComponent as Neutral } from "../../Assets/iconsv2/moodIcons/neutral.svg";
import { ReactComponent as Romantic } from "../../Assets/iconsv2/moodIcons/romantic.svg";
import { ReactComponent as Humorous } from "../../Assets/iconsv2/moodIcons/humorous.svg";
import { ReactComponent as Casual } from "../../Assets/iconsv2/moodIcons/casual.svg";
import { ReactComponent as Supportive } from "../../Assets/iconsv2/moodIcons/supportive.svg";
import { ReactComponent as Business } from "../../Assets/iconsv2/moodIcons/business.svg";
import { ReactComponent as Formal } from "../../Assets/iconsv2/moodIcons/formal.svg";

import { useAuthService } from "../../Context/AuthContext";
import { useWebSocketDataService } from "../../Context/WebsocketContext";
import {
  ROMANTIC_MOOD,
  HUMOROUS_MOOD,
  CASUAL_MOOD,
  NEUTRAL_MOOD,
  SUPPORTIVE_MOOD,
  BUSINESS_MOOD,
  FORMAL_MOOD,
  VOICE_SPEED_1X,
  VOICE_SPEED_1_25X,
  VOICE_SPEED_1_5X,
} from "../../Constants/constantValues";
import LocalStorage from "../../Helpers/LocalStorage";
import { useTranslate } from '@tolgee/react';

import VoiceControlSection from "../GenericComponents/VoiceControlSection";
import VolumeStepper from "../GenericComponents/VolumeStepper";
import DimVoiceToggle from "../GenericComponents/DimVoiceToggle";
import SpeedControlCapsule from "../GenericComponents/SpeedControlCapsule";
import MoodIconsBar from "../GenericComponents/MoodIconsBar";

const moods = [
  ROMANTIC_MOOD,
  HUMOROUS_MOOD,
  CASUAL_MOOD,
  NEUTRAL_MOOD,
  SUPPORTIVE_MOOD,
  BUSINESS_MOOD,
  FORMAL_MOOD,
];

const speedOptions = [
  { label: "1.00x", value: VOICE_SPEED_1X },
  { label: "1.25x", value: VOICE_SPEED_1_25X },
  { label: "1.50x", value: VOICE_SPEED_1_5X },
];

const convertSpeedToNumeric = (speedConstant) => {
  switch (speedConstant) {
    case VOICE_SPEED_1X: return 1.0;
    case VOICE_SPEED_1_25X: return 1.25;
    case VOICE_SPEED_1_5X: return 1.5;
    default: return 1.0;
  }
};

const VoiceControlsPanel = ({
  incomingVolume,
  dimEnabled,
  emotiiVolume,
  onIncomingVolumeChange,
  onEmotiiVolumeChange,
  setEmotiiVoice,
  emotiiVoice,
  isConference = false,
  isConferenceAttendee = false,
  conferenceTtsPlaybackRate,
  onConferenceTtsSpeedChange,
  activeCallRef,
  standalone = false,
}) => {
  const { t } = useTranslate();
  const { user, updateUser } = useAuthService();
  const { sendMoodMessage } = useWebSocketDataService();

  const [isVoiceSettingsExpanded, setIsVoiceSettingsExpanded] = useState(true);

  const [emotiiVoiceSpeed, setEmotiiVoiceSpeed] = useState(() => {
    const speedValue = isConference
      ? conferenceTtsPlaybackRate
      : user?.emotii_voice_speed;
    if (speedValue) {
      if (speedValue === 1.0) return VOICE_SPEED_1X;
      if (speedValue === 1.25) return VOICE_SPEED_1_25X;
      if (speedValue === 1.5) return VOICE_SPEED_1_5X;
    }
    return VOICE_SPEED_1X;
  });

  const [translationMood, setTranslationMood] = useState(NEUTRAL_MOOD);

  const handleIncomingVolumeChange = (e) => {
    const value = parseInt(e.target.value, 10);

    if (onIncomingVolumeChange) {
      onIncomingVolumeChange(value, dimEnabled);
    }
  };

  useEffect(() => {
    if (isMobile && !dimEnabled && onIncomingVolumeChange) {
      onIncomingVolumeChange(incomingVolume, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDimVoiceToggle = () => {
    const newValue = !dimEnabled;

    if (onIncomingVolumeChange) {
      onIncomingVolumeChange(incomingVolume, newValue);
    }
  };

  const handleEmotiiVolumeChange = (e) => {
    const value = parseInt(e.target.value, 10);

    if (isConference) {
      if (onEmotiiVolumeChange) {
        onEmotiiVolumeChange(value);
      }
      return;
    }

    if (value === 0) {
      setEmotiiVoice(false, "slider_zero");
      LocalStorage.setEmotiiVoice(false);
    } else if (!emotiiVoice) {
      setEmotiiVoice(true, "slider_nonzero");
      LocalStorage.setEmotiiVoice(true);
    }

    if (onEmotiiVolumeChange) {
      onEmotiiVolumeChange(value);
    }
  };

  const handleSpeedChange = (newSpeed) => {
    const previousSpeed = emotiiVoiceSpeed;
    setEmotiiVoiceSpeed(newSpeed);

    const numericSpeed = convertSpeedToNumeric(newSpeed);

    if (isConference) {
      onConferenceTtsSpeedChange?.(numericSpeed);
      return;
    }

    updateUser(
      { emotii_voice_speed: numericSpeed },
      () => {

      },
      (error) => {
        console.error("Failed to update speed:", error);
        setEmotiiVoiceSpeed(previousSpeed);
      }
    );
  };

  const handleMoodSelect = (mood) => {
    setTranslationMood(mood);
    if (activeCallRef?.current?.details?.uuid) {
      const callUuid = activeCallRef.current.details.uuid;
      sendMoodMessage(callUuid, mood);
    }
  };

  const getMoodIcon = (mood) => {
    switch (mood) {
      case ROMANTIC_MOOD: return <Romantic />;
      case HUMOROUS_MOOD: return <Humorous />;
      case CASUAL_MOOD: return <Casual />;
      case NEUTRAL_MOOD: return <Neutral />;
      case SUPPORTIVE_MOOD: return <Supportive />;
      case BUSINESS_MOOD: return <Business />;
      case FORMAL_MOOD: return <Formal />;
      default: return <Neutral />;
    }
  };

  const getMoodLabel = (mood) => {
    switch (mood) {
      case ROMANTIC_MOOD: return t('message_mood.romantic');
      case HUMOROUS_MOOD: return t('message_mood.humorous');
      case CASUAL_MOOD: return t('message_mood.casual');
      case NEUTRAL_MOOD: return t('message_mood.neutral');
      case SUPPORTIVE_MOOD: return t('message_mood.supportive');
      case BUSINESS_MOOD: return t('message_mood.business');
      case FORMAL_MOOD: return t('message_mood.formal');
      default: return t('message_mood.neutral');
    }
  };

  const expanded = standalone ? true : isVoiceSettingsExpanded;

  return (
    <VoicePanel $standalone={standalone}>
      <PanelHeader
        $clickable={!standalone}
        onClick={standalone ? undefined : () => setIsVoiceSettingsExpanded(!isVoiceSettingsExpanded)}
      >
        <Title>{t('call_modal.voice_and_style')}</Title>
        {!standalone && (
          <CollapseBtn expanded={expanded}>
            <ChevronUpIcon />
          </CollapseBtn>
        )}
      </PanelHeader>

      {expanded && (
        <PanelBody>
          <VoiceControlSection
            leftLabel={t('call_modal.incoming_voice')}
            leftControl={
              <VolumeStepper
                value={incomingVolume}
                onChange={handleIncomingVolumeChange}
              />
            }
            {...(!isMobile
              ? {
                  rightLabel: t('call_modal.dim_voice'),
                  rightControl: (
                    <DimVoiceToggle
                      active={dimEnabled}
                      onClick={handleDimVoiceToggle}
                    />
                  ),
                }
              : {})}
          />

          <VoiceControlSection
            leftLabel={t('call_modal.emotii_voice')}
            leftControl={
              <VolumeStepper
                value={emotiiVolume}
                onChange={handleEmotiiVolumeChange}
              />
            }
            {...(!isConference || !isConferenceAttendee
              ? {
                  rightLabel: t('call_modal.speed'),
                  rightControl: (
                    <SpeedControlCapsule
                      speed={emotiiVoiceSpeed}
                      onSpeedChange={handleSpeedChange}
                      speeds={speedOptions}
                    />
                  ),
                }
              : {})}
          />
        </PanelBody>
      )}
      {!isConference && (
        <MoodSection>
          <MoodIconsBar
            moods={moods}
            selectedMood={translationMood}
            onMoodSelect={handleMoodSelect}
            getMoodIcon={getMoodIcon}
            getMoodLabel={getMoodLabel}
          />
        </MoodSection>
      )}
    </VoicePanel>
  );
};

export default VoiceControlsPanel;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const VoicePanel = styled.div`
  display: flex;
  flex-direction: column;
  height: ${({ $standalone }) => $standalone ? "100%" : "auto"};
  background: ${({ theme }) => theme.callComponent.participantsListBackground};
  border-radius: 0;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
  user-select: none;
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  margin: 0;
`;

const CollapseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  transform: ${({ expanded }) => expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.primaryButton.borderColor};
  }
`;

const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
  padding: 0 16px 0 16px;
  animation: ${fadeIn} 0.3s ease;
`;

const MoodSection = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;
